import React, { forwardRef } from 'react';
import { cn } from '../../../utils/cn';

/**
 * GlassButton - Executive design system button component
 *
 * A premium button component with glass morphism effects, gradient backgrounds,
 * and smooth transitions. Supports multiple variants, sizes, icons, and loading states.
 *
 * @example
 * ```tsx
 * <GlassButton variant="primary" size="lg" leftIcon={<PlusIcon />}>
 *   Create Epic
 * </GlassButton>
 *
 * <GlassButton variant="gradient" isLoading>
 *   Saving...
 * </GlassButton>
 * ```
 */

export interface GlassButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: 'primary' | 'secondary' | 'ghost' | 'gradient';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Icon to display on the left side */
  leftIcon?: React.ReactNode;
  /** Icon to display on the right side */
  rightIcon?: React.ReactNode;
  /** Show loading spinner and disable interaction */
  isLoading?: boolean;
  /** Expand button to full width of container */
  fullWidth?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Button content */
  children: React.ReactNode;
}

const sizeClasses = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-base',
  lg: 'px-6 py-3 text-lg',
};

const variantClasses = {
  primary: cn(
    'bg-[var(--button-primary-bg)]',
    'text-[var(--button-primary-text)] font-medium',
    'shadow-md hover:shadow-lg',
    'hover:bg-[var(--button-primary-hover)]',
    'active:brightness-90',
    'disabled:opacity-50 disabled:cursor-not-allowed'
  ),
  secondary: cn(
    'glass font-medium',
    'text-muted-foreground',
    'hover:bg-bg-hover',
    'active:bg-bg-tertiary',
    'disabled:opacity-50 disabled:cursor-not-allowed'
  ),
  ghost: cn(
    'bg-transparent',
    'text-muted-foreground',
    'hover:bg-bg-hover',
    'active:bg-bg-tertiary',
    'disabled:opacity-50 disabled:cursor-not-allowed'
  ),
  gradient: cn(
    'bg-gradient-to-r from-[var(--accent-blue)] via-[var(--accent-violet,#8b5cf6)] to-[var(--accent-violet,#8b5cf6)]',
    'text-white font-semibold',
    'shadow-[var(--shadow-glow-blue)]',
    'hover:shadow-[0_12px_40px_-8px_var(--accent-blue)]',
    'hover:scale-[1.02]',
    'active:scale-[0.98]',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100'
  ),
};

/**
 * LoadingSpinner component for button loading state
 */
const LoadingSpinner = ({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) => {
  const spinnerSize = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  }[size];

  return (
    <svg
      className={cn('animate-spin', spinnerSize)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

/**
 * GlassButton Component
 *
 * Features:
 * - Four visual variants (primary, secondary, ghost, gradient)
 * - Three size options (sm, md, lg)
 * - Left and right icon support
 * - Loading state with spinner
 * - Full width option
 * - Smooth transitions and hover effects
 * - Theme-aware (light/dark mode support)
 * - Accessible with proper ARIA attributes
 */
export const GlassButton = forwardRef<HTMLButtonElement, GlassButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      leftIcon,
      rightIcon,
      isLoading = false,
      fullWidth = false,
      className,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const buttonClasses = cn(
      // Base styles
      'inline-flex items-center justify-center gap-2',
      'rounded-xl',
      'transition-all duration-200',
      'focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:ring-offset-2',
      'dark:focus:ring-offset-[var(--bg)]',
      // Size
      sizeClasses[size],
      // Variant
      variantClasses[variant],
      // Full width
      fullWidth && 'w-full',
      // Custom classes
      className
    );

    return (
      <button
        ref={ref}
        className={buttonClasses}
        disabled={disabled || isLoading}
        aria-busy={isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <LoadingSpinner size={size} />
            <span>{children}</span>
          </>
        ) : (
          <>
            {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
            <span>{children}</span>
            {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
          </>
        )}
      </button>
    );
  }
);

GlassButton.displayName = 'GlassButton';
